import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../api/client';

type Aba = 'overview' | 'emails' | 'ab' | 'log' | 'config';

interface Config {
  pausa: boolean;
  reenvioAtivo: boolean;
  reenvioDias: number;
  backfillFeitoEm: string | null;
}
interface Overview {
  totalAssinantes: number;
  status: { ativo: number; concluido: number; descadastrado: number; suprimido: number };
  grupos: { A: number; B: number };
  supressoes: { descadastro: number; bounce: number; reclamacao: number };
  funil: { posicao: number; enviados: number; pendentes: number }[];
  config: Config;
}
interface EmailStat {
  emailKey: string;
  posicao: number;
  assunto: string;
  tipo: 'mini' | 'tiragem';
  grupo: 'A' | 'B' | null;
  enviados: number;
  abertos: number;
  clicados: number;
  pulados: number;
  pendentes: number;
  falhou: number;
  taxaAbertura: number;
  taxaClique: number;
}
interface AbGrupo {
  enviados: number;
  abertos: number;
  clicados: number;
  taxaAbertura: number;
  taxaClique: number;
}
interface AbResp {
  porPosicao: { posicao: number; A: EmailStat | null; B: EmailStat | null }[];
  consolidado: { A: AbGrupo; B: AbGrupo };
}
interface LogItem {
  id: string;
  email: string | null;
  grupo: 'A' | 'B' | null;
  posicao: number;
  assunto: string;
  status: string;
  reenvio: boolean;
  enviadoEm: string | null;
  aberto: boolean;
  clicado: boolean;
  erro: string | null;
  atualizadoEm: string;
}

/* ---------- primitivos visuais (tema celestial) ---------- */

function GlassCard({
  children,
  className = '',
  delay = 0,
  hover = false,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  hover?: boolean;
}) {
  return (
    <div
      className={`nut-glass nut-rise rounded-2xl ${hover ? 'nut-glass-hover' : ''} ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function Stat({
  icon,
  titulo,
  valor,
  sub,
  delay = 0,
}: {
  icon: string;
  titulo: string;
  valor: ReactNode;
  sub?: string;
  delay?: number;
}) {
  return (
    <GlassCard hover delay={delay} className="p-4">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.14em] text-white/45">{titulo}</div>
        <span className="text-base opacity-70">{icon}</span>
      </div>
      <div className="mt-2 font-serif text-3xl font-medium nut-gold-text">{valor}</div>
      {sub && <div className="mt-1 text-xs text-white/40">{sub}</div>}
    </GlassCard>
  );
}

function Loading() {
  return (
    <div className="flex items-center gap-2 py-10 text-white/50">
      <span className="animate-pulse">✦</span> consultando as cartas…
    </div>
  );
}
function ErroBox() {
  return <div className="py-10 text-rose-300/80">Não consegui carregar agora. Tenta de novo.</div>;
}

const ABAS: { id: Aba; label: string; icon: string }[] = [
  { id: 'overview', label: 'Visão geral', icon: '☾' },
  { id: 'emails', label: 'E-mails', icon: '✉' },
  { id: 'ab', label: 'Teste A/B', icon: '🃏' },
  { id: 'log', label: 'Log', icon: '✦' },
  { id: 'config', label: 'Config', icon: '⚙' },
];

export function NutricaoPage() {
  const [aba, setAba] = useState<Aba>('overview');
  const cfg = useQuery<Config>({
    queryKey: ['nutricao-config'],
    queryFn: () => request<Config>('/admin/nutricao/config'),
    refetchInterval: 30_000,
  });
  const pausa = cfg.data?.pausa ?? true;

  return (
    <div className="nut-root p-3 sm:p-6">
      <div className="nut-sky nut-stars relative overflow-hidden rounded-3xl px-4 py-6 sm:px-8 sm:py-8">
        {/* Hero */}
        <header className="relative z-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="grid h-14 w-14 place-items-center rounded-2xl nut-glass text-2xl">
              ☾
            </div>
            <div>
              <h1 className="font-serif text-3xl font-medium leading-tight nut-gold-text">
                Sequência de Nutrição
              </h1>
              <p className="mt-0.5 text-sm text-white/55">
                10 mensagens ao longo de 6 semanas · segundas e quintas, 11h50 · Semente Energética
              </p>
            </div>
          </div>
          {cfg.data && (
            <div className="nut-glass inline-flex items-center gap-2.5 rounded-full px-4 py-2.5">
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  pausa ? 'bg-amber-300 nut-dot-pause' : 'bg-emerald-400 nut-dot-live'
                }`}
              />
              <span className="text-sm font-medium text-white/85">
                {pausa ? 'Pausada' : 'No ar'}
              </span>
            </div>
          )}
        </header>

        {/* Tabs */}
        <nav className="relative z-10 mt-7 flex flex-wrap gap-1.5">
          {ABAS.map((a) => {
            const ativo = aba === a.id;
            return (
              <button
                key={a.id}
                onClick={() => setAba(a.id)}
                aria-current={ativo ? 'page' : undefined}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium outline-none transition-all focus-visible:ring-2 focus-visible:ring-amber-200/60 ${
                  ativo
                    ? 'bg-gradient-to-r from-[#f3ddab] to-[#e8c87e] text-[#20153a] shadow-[0_8px_22px_-8px_rgba(232,200,126,0.7)]'
                    : 'text-white/55 hover:bg-white/5 hover:text-white/90'
                }`}
              >
                <span className={ativo ? '' : 'opacity-70'}>{a.icon}</span>
                {a.label}
              </button>
            );
          })}
        </nav>

        {/* Conteúdo */}
        <div className="relative z-10 mt-6">
          {aba === 'overview' && <OverviewTab />}
          {aba === 'emails' && <EmailsTab />}
          {aba === 'ab' && <AbTab />}
          {aba === 'log' && <LogTab />}
          {aba === 'config' && <ConfigTab />}
        </div>
      </div>
    </div>
  );
}

function OverviewTab() {
  const q = useQuery<Overview>({
    queryKey: ['nutricao-overview'],
    queryFn: () => request<Overview>('/admin/nutricao/overview'),
    refetchInterval: 30_000,
  });
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErroBox />;
  const d = q.data;
  const maxFunil = Math.max(1, ...d.funil.map((f) => f.enviados + f.pendentes));
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat icon="✦" titulo="Assinantes" valor={d.totalAssinantes} delay={0} />
        <Stat icon="☾" titulo="Ativos" valor={d.status.ativo} delay={60} />
        <Stat icon="✧" titulo="Concluíram" valor={d.status.concluido} delay={120} />
        <Stat
          icon="◦"
          titulo="Saíram"
          valor={d.status.descadastrado + d.status.suprimido}
          sub={`${d.status.descadastrado} descadastro · ${d.status.suprimido} bounce/spam`}
          delay={180}
        />
        <Stat icon="A" titulo="Grupo A" valor={d.grupos.A} delay={240} />
        <Stat icon="B" titulo="Grupo B" valor={d.grupos.B} delay={300} />
        <Stat icon="✉" titulo="Bounces" valor={d.supressoes.bounce} delay={360} />
        <Stat icon="⚑" titulo="Reclamações" valor={d.supressoes.reclamacao} delay={420} />
      </div>

      <GlassCard delay={200} className="p-5">
        <div className="mb-4 flex items-center gap-2 text-sm font-medium text-white/80">
          <span className="text-amber-200/80">🃏</span> Jornada por e-mail
          <span className="text-white/35">— quantos já receberam cada carta</span>
        </div>
        <div className="space-y-2">
          {d.funil.map((f) => (
            <div key={f.posicao} className="flex items-center gap-3 text-sm">
              <span className="w-9 font-serif text-white/50">#{f.posicao}</span>
              <div className="h-6 flex-1 overflow-hidden rounded-full bg-white/[0.06]">
                <div
                  className="nut-bar-fill h-6 rounded-full"
                  style={{ width: `${Math.max(2, (f.enviados / maxFunil) * 100)}%` }}
                />
              </div>
              <span className="w-32 text-right text-white/55">
                <span className="text-white/90">{f.enviados}</span> env
                <span className="mx-1 text-white/25">·</span>
                {f.pendentes} pend
              </span>
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  );
}

function tipoTag(tipo: string, grupo: string | null) {
  if (tipo === 'tiragem')
    return (
      <span className="rounded-full bg-amber-300/15 px-2 py-0.5 text-xs text-amber-200/90">
        Tiragem {grupo}
      </span>
    );
  return <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs text-white/60">Mini</span>;
}

function EmailsTab() {
  const q = useQuery<EmailStat[]>({
    queryKey: ['nutricao-emails'],
    queryFn: () => request<EmailStat[]>('/admin/nutricao/emails'),
    refetchInterval: 30_000,
  });
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErroBox />;
  return (
    <GlassCard className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-amber-200/60">
            <th className="p-3 font-medium">#</th>
            <th className="p-3 font-medium">Assunto</th>
            <th className="p-3 font-medium">Tipo</th>
            <th className="p-3 text-right font-medium">Enviados</th>
            <th className="p-3 text-right font-medium">Abertura</th>
            <th className="p-3 text-right font-medium">Clique</th>
            <th className="p-3 text-right font-medium">Pulados</th>
            <th className="p-3 text-right font-medium">Pend.</th>
            <th className="p-3 text-right font-medium">Falhou</th>
          </tr>
        </thead>
        <tbody>
          {q.data.map((e) => (
            <tr
              key={e.emailKey}
              className="border-t border-white/[0.06] text-white/80 transition-colors hover:bg-white/[0.04]"
            >
              <td className="p-3 font-serif text-white/50">{e.posicao}</td>
              <td className="max-w-xs truncate p-3" title={e.assunto}>
                {e.assunto}
              </td>
              <td className="p-3">{tipoTag(e.tipo, e.grupo)}</td>
              <td className="p-3 text-right text-white/90">{e.enviados}</td>
              <td className="p-3 text-right">
                <span className="text-emerald-300/90">{e.taxaAbertura}%</span>{' '}
                <span className="text-white/35">({e.abertos})</span>
              </td>
              <td className="p-3 text-right">
                <span className="text-amber-200/90">{e.taxaClique}%</span>{' '}
                <span className="text-white/35">({e.clicados})</span>
              </td>
              <td className="p-3 text-right text-white/45">{e.pulados}</td>
              <td className="p-3 text-right text-white/45">{e.pendentes}</td>
              <td className="p-3 text-right text-rose-300/80">{e.falhou || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </GlassCard>
  );
}

function AbTab() {
  const q = useQuery<AbResp>({
    queryKey: ['nutricao-ab'],
    queryFn: () => request<AbResp>('/admin/nutricao/ab'),
    refetchInterval: 30_000,
  });
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErroBox />;
  const { consolidado, porPosicao } = q.data;
  const aMelhor = consolidado.A.taxaAbertura >= consolidado.B.taxaAbertura;

  const painel = (nome: string, desc: string, g: AbGrupo, vencedor: boolean, delay: number) => (
    <GlassCard
      hover
      delay={delay}
      className={`p-5 ${vencedor ? 'ring-1 ring-amber-300/50 shadow-[0_0_40px_-12px_rgba(232,200,126,0.4)]' : ''}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="font-serif text-lg text-white/90">{nome}</div>
          <div className="text-xs text-white/45">{desc}</div>
        </div>
        {vencedor && (
          <span className="rounded-full bg-amber-300/15 px-2.5 py-1 text-xs text-amber-200">
            ✦ na frente
          </span>
        )}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        {[
          { v: `${g.taxaAbertura}%`, l: 'abertura' },
          { v: `${g.taxaClique}%`, l: 'clique' },
          { v: g.enviados, l: 'enviados' },
        ].map((c) => (
          <div key={c.l}>
            <div className="font-serif text-2xl nut-gold-text">{c.v}</div>
            <div className="text-[11px] uppercase tracking-wide text-white/40">{c.l}</div>
          </div>
        ))}
      </div>
    </GlassCard>
  );

  return (
    <div className="space-y-5">
      <div className="text-sm text-white/60">
        Consolidado das Tiragens <span className="text-white/85">#5 e #10</span>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {painel('Grupo A', 'Decisões Difíceis', consolidado.A, aMelhor, 0)}
        {painel('Grupo B', 'Sensação de Virada', consolidado.B, !aMelhor, 80)}
      </div>
      <GlassCard delay={160} className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-amber-200/60">
              <th className="p-3 font-medium">Tiragem</th>
              <th className="p-3 text-right font-medium">A · abertura</th>
              <th className="p-3 text-right font-medium">A · clique</th>
              <th className="p-3 text-right font-medium">B · abertura</th>
              <th className="p-3 text-right font-medium">B · clique</th>
            </tr>
          </thead>
          <tbody>
            {porPosicao.map((p) => (
              <tr key={p.posicao} className="border-t border-white/[0.06] text-white/80">
                <td className="p-3 font-serif">#{p.posicao}</td>
                <td className="p-3 text-right">{p.A ? `${p.A.taxaAbertura}%` : '—'}</td>
                <td className="p-3 text-right">{p.A ? `${p.A.taxaClique}%` : '—'}</td>
                <td className="p-3 text-right">{p.B ? `${p.B.taxaAbertura}%` : '—'}</td>
                <td className="p-3 text-right">{p.B ? `${p.B.taxaClique}%` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </GlassCard>
    </div>
  );
}

function LogTab() {
  const q = useQuery<LogItem[]>({
    queryKey: ['nutricao-log'],
    queryFn: () => request<LogItem[]>('/admin/nutricao/log?limit=100'),
    refetchInterval: 20_000,
  });
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErroBox />;
  const cor: Record<string, string> = {
    enviado: 'text-emerald-300/90',
    pulado: 'text-white/40',
    cancelado: 'text-white/40',
    falhou: 'text-rose-300/90',
  };
  return (
    <GlassCard className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-amber-200/60">
            <th className="p-3 font-medium">Quando</th>
            <th className="p-3 font-medium">E-mail</th>
            <th className="p-3 font-medium">#</th>
            <th className="p-3 font-medium">Status</th>
            <th className="p-3 font-medium">Interação</th>
          </tr>
        </thead>
        <tbody>
          {q.data.map((l) => (
            <tr
              key={l.id}
              className="border-t border-white/[0.06] text-white/80 transition-colors hover:bg-white/[0.04]"
            >
              <td className="whitespace-nowrap p-3 text-white/45">
                {new Date(l.atualizadoEm).toLocaleString('pt-BR')}
              </td>
              <td className="p-3">{l.email ?? '—'}</td>
              <td className="p-3 font-serif text-white/50">
                {l.posicao}
                {l.reenvio ? 'R' : ''}
                {l.grupo ? ` · ${l.grupo}` : ''}
              </td>
              <td className={`p-3 font-medium ${cor[l.status] ?? ''}`}>
                {l.status}
                {l.erro ? <span className="block text-xs text-rose-300/60">{l.erro}</span> : null}
              </td>
              <td className="p-3">
                {l.aberto ? '👁' : ''} {l.clicado ? '🖱' : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </GlassCard>
  );
}

function Switch({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className={`relative h-7 w-12 shrink-0 rounded-full outline-none transition-colors focus-visible:ring-2 focus-visible:ring-amber-200/60 ${
        on ? 'bg-emerald-400/80' : 'bg-white/15'
      }`}
    >
      <span
        className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${
          on ? 'left-6' : 'left-1'
        }`}
      />
    </button>
  );
}

function ConfigTab() {
  const qc = useQueryClient();
  const cfg = useQuery<Config>({
    queryKey: ['nutricao-config'],
    queryFn: () => request<Config>('/admin/nutricao/config'),
  });
  const contar = useQuery<{ totalSemente: number; novos: number }>({
    queryKey: ['nutricao-backfill-contar'],
    queryFn: () => request('/admin/nutricao/backfill/contar'),
  });
  const salvar = useMutation({
    mutationFn: (body: Partial<Config>) =>
      request('/admin/nutricao/config', { method: 'PUT', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nutricao-config'] });
    },
    onError: (e: unknown) => alert(`Falha ao salvar: ${e instanceof Error ? e.message : e}`),
  });
  const backfill = useMutation({
    mutationFn: () => request('/admin/nutricao/backfill', { method: 'POST' }),
    onSuccess: (r: unknown) => {
      const d = r as { totalCandidatos?: number };
      alert(`Backfill iniciado em background (${d.totalCandidatos ?? '?'} candidatos).`);
    },
    onError: (e: unknown) => alert(`Falha: ${e instanceof Error ? e.message : e}`),
  });
  if (!cfg.data) return <Loading />;
  const c = cfg.data;

  return (
    <div className="max-w-2xl space-y-4">
      <GlassCard delay={0} className="divide-y divide-white/[0.07] p-5">
        <label className="flex items-center justify-between gap-4 pb-4">
          <div>
            <div className="font-serif text-lg text-white/90">Pausar sequência</div>
            <div className="text-sm text-white/50">
              Pausada, nenhum e-mail é disparado (novos assinantes continuam entrando).
            </div>
          </div>
          <Switch on={c.pausa} onChange={(v) => salvar.mutate({ pausa: v })} />
        </label>
        <label className="flex items-center justify-between gap-4 pt-4">
          <div>
            <div className="font-serif text-lg text-white/90">Reenvio pra não-abridores</div>
            <div className="text-sm text-white/50">
              Reenvia mini-produtos não abertos após {c.reenvioDias} dias, com assunto alternativo.
            </div>
          </div>
          <Switch on={c.reenvioAtivo} onChange={(v) => salvar.mutate({ reenvioAtivo: v })} />
        </label>
      </GlassCard>

      <GlassCard delay={80} className="space-y-3 p-5">
        <div className="font-serif text-lg text-white/90">Backfill da base</div>
        <div className="text-sm text-white/55">
          Inscreve os alunos da Semente que ainda não estão na sequência (começam na próxima
          segunda).{' '}
          {contar.data && (
            <span className="text-amber-200/90">
              {contar.data.novos} novos de {contar.data.totalSemente}.
            </span>
          )}
        </div>
        <div className="text-xs text-white/35">
          Último backfill:{' '}
          {c.backfillFeitoEm ? new Date(c.backfillFeitoEm).toLocaleString('pt-BR') : 'nunca'}
        </div>
        <button
          onClick={() => {
            if (confirm('Inscrever todos os alunos elegíveis na sequência?')) backfill.mutate();
          }}
          disabled={backfill.isPending}
          className="rounded-xl bg-gradient-to-r from-[#f3ddab] to-[#e8c87e] px-5 py-2.5 text-sm font-semibold text-[#20153a] shadow-[0_8px_22px_-8px_rgba(232,200,126,0.7)] outline-none transition-transform hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-amber-200/60 disabled:opacity-50"
        >
          {backfill.isPending ? 'Iniciando…' : '✦ Rodar backfill'}
        </button>
      </GlassCard>
    </div>
  );
}
